import * as React from 'react';
import {
    Create,
    required,
    TextInput,
    ArrayInput,
    SimpleFormIterator,
    BooleanInput,
    SimpleForm,
    SelectInput,
    useNotify,
    ReferenceInput,
    AutocompleteInput,
    useSimpleFormIteratorItem,
    useRedirect,
    NumberInput,
} from 'react-admin';

import { BASE_URL, QUESTION_TYPES } from '../../common/constants';
import HTMLEditorInput, { validHtml } from '../../common/inputs/HTMLEditorInput';
import FileUpload from '../../common/inputs/FileUpload';
import { Tabs, Tab, Box, Button } from '@mui/material';
import { TabPanel } from '../batches/BatchesShow';
import { Checkbox, ListItemText, MenuItem, Select } from '@mui/material';
import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { goApi } from '../../services';
import { getLanguages } from '../../services/testSeries-service';
import { apiRoutes } from '../../services/apiRoutes';
import { MasterDataAutoComplete } from '../../common/inputs/autoCompleteInputs/MasterDataAutoComplete';
import { Videos } from '../../custom';

export const videoNameRender = (choice: Videos) => `${choice.title}| ${choice.code}`;
export const filterToQueryVideo = (searchText: string) =>
    searchText ? { code: `${searchText}` } : { code: `${'NOT_AVAILABLE'}` };

const useQueryParams = () => {
    const { search } = useLocation();
    return new URLSearchParams(search);
};

function transformData(data: any, languages: string[]) {
    const formData = {
        ...data,
    };

    if (!formData.options || formData.options.length === 0) {
        throw new Error('Please add at least one option');
    }

    if (formData.type === 'SINGLE') {
        const correctOptions = formData.options?.filter((opt: any) => opt.isCorrect);
        if (correctOptions?.length !== 1) {
            throw new Error('Exactly one correct answer should be selected');
        }
    }

    if (formData.type === 'MULTIPLE') {
        const correctOptions = formData.options?.filter((opt: any) => opt.isCorrect);
        if (!correctOptions?.length) {
            throw new Error(
                'At least one correct answer should be selected for MULTIPLE type questions',
            );
        }
    }

    const formattedData: any = {};
    const languagesOptions: any = {};

    languages.forEach(language => {
        languagesOptions[language] = formData.options.map((option: any, index: number) => {
            const prefix = `option-${index} ${language} `;
            const optionFields: any = {};

            for (const key in formData) {
                if (key.startsWith(prefix)) {
                    optionFields[key.replace(prefix, '')] = formData[key];
                    delete formData[key];
                }
            }
            return { ...option, ...optionFields };
        });
    });

    for (const key in formData) {
        const language = languages.find(lang => key.startsWith(lang + ' '));
        if (language) {
            if (!formattedData[language]) {
                formattedData[language] = { language, options: languagesOptions[language] || [] };
            }
            const fieldKey = key.replace(language + ' ', '');
            formattedData[language][fieldKey] = formData[key];
            delete formData[key];
        }
    }

    const createData = Object.assign({ languages: Object.values(formattedData) }, formData);
    delete createData.options;

    return createData;
}

const LanguageFields = ({ language }: { language: string; selectedType: string }) => {
    return (
        <>
            <FileUpload source="image" type="VIDEO_THUMBNAIL" typeId="" />
            <TextInput key={`name-input-${language}`} source={`${language} name`} fullWidth />
            <HTMLEditorInput source={`${language} content`} fullWidth />
            <HTMLEditorInput source={`${language} solutionDescription`} fullWidth />
            <ReferenceInput name="Video" source={`${language} video`} reference="videos">
                <AutocompleteInput
                    optionText={videoNameRender}
                    filterToQuery={filterToQueryVideo}
                    limitChoicesToValue
                    debounce={500}
                    fullWidth
                />
            </ReferenceInput>
            <FileUpload source="solutionImage" type="VIDEO_THUMBNAIL" typeId="" />
        </>
    );
};

const OptionUI = (props: any) => {
    const { index } = useSimpleFormIteratorItem();
    const { selectedType, lang } = props;

    if (!selectedType) return null;

    if (selectedType === 'NUMERIC') {
        return (
            <TextInput fullWidth source={`option-${index} ${lang} solutionText`} type="number" />
        );
    }

    return (
        <>
            <FileUpload
                source={`option-${index} ${lang} image`}
                label="image"
                type="VIDEO_THUMBNAIL"
                typeId=""
            />
            <HTMLEditorInput
                label="Content"
                fullWidth
                source={`option-${index} ${lang} content`}
                validate={[validHtml]}
            />
        </>
    );
};

const OptionsFields = ({
    languages,
    selectedType,
}: {
    languages: string[];
    selectedType: string;
}) => {
    const [value, setValue] = React.useState(0);

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };

    return (
        <Box sx={{ width: '100%' }}>
            <ArrayInput
                source="options"
                sx={{
                    border: '1px solid #aaaaaa',
                    padding: '10px',
                    borderRadius: '10px',
                    marginBottom: '20px',
                }}
            >
                <SimpleFormIterator source="options">
                    <BooleanInput
                        defaultValue={false}
                        fullWidth
                        label="Is Correct"
                        source="isCorrect"
                    />
                    <NumberInput
                        fullWidth
                        source={`displayOrder`}
                        label="Display order"
                        defaultValue={0}
                    />
                    <Tabs value={value} onChange={handleChange}>
                        {languages.map(lang => (
                            <Tab key={lang} label={lang} />
                        ))}
                    </Tabs>
                    {languages.map((lang, index) => (
                        <TabPanel key={lang} value={value} index={index}>
                            <OptionUI selectedType={selectedType} lang={lang} index={index} />
                        </TabPanel>
                    ))}
                </SimpleFormIterator>
            </ArrayInput>
        </Box>
    );
};

const QuestionBankCreate = () => {
    const [selectedType, setSelectedType] = React.useState<string>('');
    const [selectedMasterClass, setSelectedMasterClass] = React.useState<string>('');
    const [selectedMasterSubject, setSelectedMasterSubject] = React.useState<string>('');
    const [selectedMasterChapter, setSelectedMasterChapter] = React.useState<string>('');
    const [selectedMasterTopic, setSelectedMasterTopic] = React.useState<string>('');
    const notify = useNotify();
    const [isLoading, setIsLoading] = React.useState(true);
    const [value, setValue] = React.useState(0);
    const redirect = useRedirect();

    const [languagesList, setLanguagesList] = React.useState<string[]>([]);
    const [selectedLanguages, setSelectedLanguages] = React.useState<string[]>(['English']);

    useEffect(() => {
        (async () => {
            const data = await getLanguages();
            const languageNames = Object.keys(data);
            setLanguagesList(languageNames);
            setSelectedLanguages(['English']);
            setIsLoading(false);
        })();
    }, []);

    const handleLanguagesChange = (event: any) => {
        let value = event.target.value as string[];

        if (selectedLanguages?.length <= 1 && value.length === 0) {
            notify(`Can not remove last language.`, { type: 'warning' });
            return;
        }

        setSelectedLanguages(value);
    };

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };

    const getUiForTab = (tabIndex: number) => {
        return (
            <LanguageFields language={selectedLanguages[tabIndex]} selectedType={selectedType} />
        );
    };

    const query = useQueryParams();
    const testSeriesId = query.get('testSeriesId');
    const testId = query.get('testId');
    const sectionId = query.get('sectionId');

    const modifiedPostSave = async (data: any) => {
        try {
            const createData = transformData(data, selectedLanguages);

            const resp = await goApi.post(apiRoutes.QUESTION_BANK, createData);
            console.log('Question creation response:', resp?.data?.questions);
            const responseQuestions = resp?.data?.questions;
            const createdQuestions = Array.isArray(responseQuestions)
                ? responseQuestions
                : Array.isArray(resp?.data)
                ? resp.data
                : [resp?.data];

            if (testSeriesId && testId && sectionId && createdQuestions.length > 0) {
                const sectionQuestions = createdQuestions.map((question: any) => ({
                    id: String(question?.questionId || question?.id || ''),
                    displayOrder: Number(question?.displayOrder ?? createData?.displayOrder ?? 1),
                    positiveMarks: Number(
                        question?.positiveMarks ?? createData?.positiveMarks ?? 1,
                    ),
                    negativeMarks: Number(
                        question?.negativeMarks ?? createData?.negativeMarks ?? 0,
                    ),
                }));

                const sectionData: {
                    sectionId: string;
                    questions: Array<{
                        id: string;
                        displayOrder: number;
                        positiveMarks: number;
                        negativeMarks: number;
                    }>;
                } = {
                    sectionId,
                    questions: sectionQuestions,
                };

                const sectionResponse = await goApi.post(
                    apiRoutes.SECTION_QUESTION_MAPPINGS(testId),
                    sectionData,
                );

                if (sectionResponse) {
                    notify('Question added to section successfully', { type: 'success' });
                    return redirect(
                        `/test-series/${testSeriesId}/tests/${testId}/sections/${sectionId}/questions`,
                    );
                } else {
                    notify('Failed to assign questions to section', { type: 'warning' });
                    return;
                }
            }

            notify('Question created successfully', { type: 'success' });
            redirect(`/questions_bank`);
        } catch (error: any) {
            notify(`Error: ${error.message}`, { type: 'warning' });
        }
    };

    if (isLoading) return <></>;

    return (
        <Box sx={{ width: '100%' }}>
            <SimpleForm onSubmit={modifiedPostSave}>
                <Box sx={{ width: '100%' }}>
                    <SelectInput
                        source="type"
                        label="Type"
                        fullWidth
                        validate={[required()]}
                        choices={QUESTION_TYPES}
                        onChange={e => setSelectedType(e.target.value)}
                    />

                    <Select
                        multiple
                        placeholder="Select Languages"
                        value={selectedLanguages}
                        onChange={handleLanguagesChange}
                        renderValue={selected => (selected as string[]).join(', ')}
                        fullWidth
                        size="small"
                        className="mb-6"
                    >
                        {languagesList.map(lang => (
                            <MenuItem key={lang} value={lang} sx={{ py: 0.5, minWidth: 400 }}>
                                <Checkbox
                                    checked={selectedLanguages.indexOf(lang) > -1}
                                    size="small"
                                />
                                <ListItemText
                                    primary={lang}
                                    primaryTypographyProps={{ fontSize: 14 }}
                                />
                            </MenuItem>
                        ))}
                    </Select>

                    <NumberInput source="positiveMarks" fullWidth />
                    <NumberInput source="negativeMarks" fullWidth />

                    <MasterDataAutoComplete
                        type="class"
                        onChange={(value: any) => setSelectedMasterClass(value || '')}
                    />

                    <MasterDataAutoComplete
                        type="subject"
                        onChange={(value: any) => {
                            setSelectedMasterSubject(value || '');
                            setSelectedMasterChapter('');
                            setSelectedMasterTopic('');
                        }}
                    />

                    {selectedMasterSubject && (
                        <MasterDataAutoComplete
                            type="chapter"
                            filterId={selectedMasterSubject}
                            onChange={(value: any) => {
                                setSelectedMasterChapter(value || '');
                                setSelectedMasterTopic('');
                            }}
                        />
                    )}

                    {selectedMasterSubject && selectedMasterChapter && (
                        <MasterDataAutoComplete
                            type="topic"
                            filterId={selectedMasterChapter}
                            subjectId={selectedMasterSubject}
                            onChange={(value: any) => setSelectedMasterTopic(value || '')}
                        />
                    )}

                    <Tabs value={value} onChange={handleChange}>
                        {selectedLanguages.map(lang => (
                            <Tab key={lang} label={lang} />
                        ))}
                    </Tabs>

                    {selectedLanguages.map((lang, index) => (
                        <TabPanel key={lang} value={value} index={index}>
                            {getUiForTab(index)}
                        </TabPanel>
                    ))}

                    <OptionsFields languages={selectedLanguages} selectedType={selectedType} />
                </Box>
            </SimpleForm>
        </Box>
    );
};

export default QuestionBankCreate;
